import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  ButtonOutlined,
  ButtonPrimary,
} from "../../../../../components/button/ButtonCustoms";
import ModalCustom from "../../../../../components/modal/ModalCustom";
import { Grid, Typography } from "@mui/material";
import InputTextField from "../../../../../components/input/inputTextField";
import { alertError, alertSuccess } from "../../../../../components/alert/swal";
import { settingLeavePeriodApi } from "../../../../../api/setting-leave-period/setting-leave-period.api";
import {
  setLoading,
  unsetLoading,
} from "../../../../../store/loading/loading.slice";
import { leaveTypeApi } from "../../../../../api/leave-type/leave-type.api";
import { dateToView } from "../../../../../utils/date.utils";
import moment from "moment";

type Props = {
  is_update: boolean;
  isOpenModal: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
  data?: any;
  is_all: boolean;
  period_id?: number;
  onFetch?: () => void;
  idEmployee: number[];
  employeeInfo?: any;
};

interface LeaveType {
  id_leave_type: number;
  id_leave_form: number;
  leave_type_name: string;
  is_accumulate: string;
  id_employee_inspector: number;
  id_employee_approve: number;
  status: string;
  created_id: number;
  created_date: string;
  modified_id: number;
  modified_date: string;
  leave_form: {
    json: string;
  };
}

interface LeaveTypeDetail {
  id_leave_employee: number;
  id_leave_type: number;
  id_employee: number;
  id_leave_period: number;
  year: string;
  leave_employee_day: number;
  leave_employee_day_use: number | null;
  leave_type: {
    id_leave_type: number;
    leave_type_name: string;
    is_accumulate: string;
    id_employee_inspector: number;
    id_employee_approve: number;
    status: string;
  };
  leave_employee_day_amount: number;
}

const ModalQuotaHoliday: React.FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const {
    isOpenModal,
    setIsOpenModal,
    is_update,
    is_all,
    data,
    period_id,
    onFetch,
    idEmployee,
    employeeInfo,
  } = props;

  const [isCanEdit, setIsCanEdit] = useState<boolean>(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [dataLeaveType, setDataLeaveType] = useState<LeaveType[]>([]);
  const [dataSingleSettingLeaveDay, setDataSingleSettingLeaveDay] =
    useState<any>({
      id_leave_period: 0,
      leave_period: "",
      leave_period_start: "",
      leave_period_end: "",
      status: "",
    });

  const [leaveTypeInputs, setLeaveTypeInputs] = useState<
    {
      id_leave_employee: number;
      holiday_amount: number;
      holiday_used: number;
      holiday_balance: number;
    }[]
  >([]);

  const getLeaveType = async (idEmployee: number[], period_id: number) => {
    const param = { limit: 100 };
    const res = await leaveTypeApi.getAllLeaveType(param);
    const data = res.data;

    if (data.status < 300) {
      setDataLeaveType(data.data.items);

      if (idEmployee && idEmployee.length === 1) {
        // Fetch the leave detail for the specific employee
        const periodDetail = await settingLeavePeriodApi.getLeaveEmployeeDetail(
          +period_id,
          idEmployee[0]
        );

        const dataPeriodDetail: LeaveTypeDetail[] =
          periodDetail.data?.data ?? [];

        // Create a mapping of leave types from the fetched detail
        const leaveTypeDetailMap = new Map(
          dataPeriodDetail.map((detail: any) => [detail.id_leave_type, detail])
        );

        // Map the initial inputs with data from the detail
        const initialInputs = data.data.items.map((item: any) => {
          const leaveDetail = leaveTypeDetailMap.get(item.id_leave_type);

          return {
            id_leave_employee: leaveDetail?.id_leave_employee || null,
            holiday_amount: leaveDetail?.leave_employee_day || null,
            holiday_used: leaveDetail?.leave_employee_day_use || null,
            holiday_balance: leaveDetail?.leave_employee_day_amount || null,
          };
        });

        setLeaveTypeInputs(initialInputs);
      } else {
        // For multiple employees or if no specific employee is selected
        const initialInputs = data.data.items.map((item: any) => ({
          id_leave_employee: null,
          holiday_amount: null,
          holiday_used: null,
          holiday_balance: null,
        }));

        setLeaveTypeInputs(initialInputs);
      }
    } else {
      setDataLeaveType([]);
    }
  };

  const handleInputChange = (
    index: number,
    field: "holiday_amount" | "holiday_used" | "holiday_balance",
    value: number
  ) => {
    const updatedInputs = [...leaveTypeInputs];
    updatedInputs[index][field] = value;

    // if (field === "holiday_amount" || field === "holiday_used") {
    //   updatedInputs[index].holiday_balance =
    //     updatedInputs[index].holiday_amount - updatedInputs[index].holiday_used;
    // }

    setLeaveTypeInputs(updatedInputs);
  };

  useEffect(() => {
    getLeaveType(idEmployee, period_id ?? 0);
  }, [idEmployee, period_id]);

  const handleSubmitByEmployee = async (period_id: number) => {
    try {
      dispatch(setLoading());
      setLoadingSubmit(true);
      const allEmployeeLeaveData = await Promise.all(
        idEmployee.map(async (employeeId) => {
          // Fetch leave details for the specific employee and period
          const periodDetail =
            await settingLeavePeriodApi.getLeaveEmployeeDetail(
              period_id,
              employeeId
            );

          const dataPeriodDetail: LeaveTypeDetail[] =
            periodDetail.data?.data ?? [];

          // Create a mapping of leave types from the fetched details
          const leaveTypeDetailMap = new Map(
            dataPeriodDetail.map((detail: any) => [
              detail.id_leave_type,
              detail,
            ])
          );

          // Map the inputs and ensure `id_leave_employee` is set only if it exists
          const mappedInputs = leaveTypeInputs.map((input, index) => {
            const leaveDetail = leaveTypeDetailMap.get(
              dataLeaveType[index].id_leave_type
            );

            return {
              id_leave_employee: leaveDetail?.id_leave_employee ?? null, // Do not set null if no id exists
              id_leave_type: dataLeaveType[index].id_leave_type,
              year: dataSingleSettingLeaveDay.leave_period,
              id_employee: employeeId,
              leave_employee_day: input.holiday_amount ?? 0,
            };
          });

          return mappedInputs;
        })
      );

      const dataBody = allEmployeeLeaveData.flat();

      const res = await settingLeavePeriodApi.updateEmployeeHoliday(
        dataBody,
        period_id
      );

      if (res.status < 300) {
        dispatch(unsetLoading());
        setLoadingSubmit(false);
        alertSuccess(
          "ตั้งค่าจำนวนวันลา",
          "ตั้งค่าจำนวนวันลา เรียบร้อยแล้ว",
          undefined,
          () => {
            dispatch(unsetLoading());
            setLoadingSubmit(false);
            setIsOpenModal(false);
            setIsCanEdit(false);
            onFetch && onFetch();
          }
        );
      }
    } catch (error) {
      alertError("ตั้งค่าวันลา ไม่สำเร็จ");
      dispatch(unsetLoading());
      setLoadingSubmit(false);
    } finally {
      dispatch(unsetLoading());
      setLoadingSubmit(false);
    }
  };

  const getSingleSettinLeavePeriod = async (id: number) => {
    const res = await settingLeavePeriodApi.getLeavePeriodById(id);
    const data = res.data;
    if (data.status < 300 && data.data) {
      setDataSingleSettingLeaveDay(data.data);
    } else {
      setDataSingleSettingLeaveDay({});
    }
  };

  useEffect(() => {
    if (period_id) {
      getSingleSettinLeavePeriod(+period_id);
    }
  }, [period_id]);

  return (
    <ModalCustom
      title={is_update ? "ตั้งค่าจำนวนวันลา" : "จำนวนวันลา"}
      subTitle={
        is_update
          ? "กรุณาเลือกรายการที่ท่านต้องการ"
          : `รอบปี ${dataSingleSettingLeaveDay.leave_period} ${moment(
            dataSingleSettingLeaveDay.leave_period_start
          ).format("DD/MM/YYYY")}-${moment(dataSingleSettingLeaveDay.leave_period_end).format("DD/MM/YYYY")}`
      }
      open={isOpenModal}
      onClose={() => {
        setIsOpenModal(false);
        setIsCanEdit(false);
      }}
      maxWidth={!is_update ? 1200 : 700} // Setting a custom width of 1200px
      component={
        <section className="grid grid-cols-12 gap-y-6">
          {!is_all && (
            <article className="col-span-12">
              {employeeInfo && (
                <>
                  <p>
                    ชื่อ-นามสกุล :{" "}

                    {/* แก้จากของ joe-dev ให้ใช้งานได้เฉยๆ */}
                    {(employeeInfo.title || "") +
                      "" + " " + (employeeInfo.first_name || "") ||
                      "" + " " + (employeeInfo.last_name || "") ||
                      ""}
                  </p>
                  <p>ตำแหน่งงาน : {employeeInfo.position_name ?? "-"} </p>
                  <p>หน่วยงาน : {employeeInfo.department_name_th ?? "-"} </p>
                </>
              )}
            </article>
          )}

          <article
            className="col-span-12"
            style={{
              maxHeight: "700px",
              overflowY: "auto",
            }}
          >
            {dataLeaveType && dataLeaveType.length > 0
              ? dataLeaveType.map((item: any, index: number) => (
                <div
                  style={{
                    backgroundColor: "#F8F8F8",
                    padding: "12px 8px",
                    borderRadius: 8,
                    textAlign: "center", // Center text
                    marginBottom: 12,
                  }}
                  key={index + 1}
                >
                  <Grid
                    container
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {!is_update ? (
                      <>
                        <Grid item xs={3}>
                          <Typography variant="body1" align="left">
                            {item.leave_type_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                          <Typography variant="body1" align="right">
                            จำนวนวันรวม
                          </Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <InputTextField
                            value={leaveTypeInputs[index]?.holiday_amount}
                            style={{
                              maxWidth: "100px",
                              textAlign: "center",
                              padding: 0,
                              margin: 0,
                            }}
                            disabled={!is_update}
                          />
                        </Grid>
                        <Grid item xs={0.5}>
                          <Typography variant="body1" align="left">
                            วัน
                          </Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                          <Typography
                            variant="body1"
                            align="right"
                            style={{
                              color: "red",
                            }}
                          >
                            ใช้แล้ว
                          </Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <InputTextField
                            value={leaveTypeInputs[index]?.holiday_used ?? 0}
                            style={{
                              maxWidth: "100px",
                              textAlign: "center",
                              padding: 0,
                              margin: 0,
                            }}
                            disabled={!is_update}
                          />
                        </Grid>
                        <Grid item xs={0.5}>
                          <Typography
                            variant="body1"
                            align="left"
                            style={{
                              color: "red",
                            }}
                          >
                            วัน
                          </Typography>
                        </Grid>
                        {/* colors */}
                        <Grid item xs={1.5}>
                          <Typography
                            variant="body1"
                            align="right"
                            style={{
                              color: "green",
                            }}
                          >
                            คงเหลือ
                          </Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <InputTextField
                            value={leaveTypeInputs[index]?.holiday_balance}
                            style={{
                              maxWidth: "100px",
                              textAlign: "center",
                              padding: 0,
                              margin: 0,
                            }}
                            disabled={!is_update}
                          />
                        </Grid>
                        <Grid item xs={0.5}>
                          <Typography
                            variant="body1"
                            align="left"
                            style={{
                              color: "green",
                            }}
                          >
                            วัน
                          </Typography>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={5}>
                          <Typography variant="body1" align="left">
                            {item.leave_type_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body1" align="right">
                            จำนวนวันรวม
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <InputTextField
                            type="number"
                            value={leaveTypeInputs[index]?.holiday_amount}
                            onchange={(e) =>
                              handleInputChange(
                                index,
                                "holiday_amount",
                                Number(e.target.value)
                              )
                            }
                            style={{
                              maxWidth: "100px",
                              textAlign: "center",
                              padding: 0,
                              margin: 0,
                            }}
                            disabled={!is_update || !isCanEdit}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="body1" align="left">
                            วัน
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </div>
              ))
              : null}
          </article>

          {!is_update ? (
            <article className="col-span-12 flex items-center justify-center gap-4 my-4">
              <ButtonOutlined
                onClick={() => setIsOpenModal(false)}
                mode="del"
                buttonName="ยกเลิก"
              />
            </article>
          ) : (
            <article className="col-span-12 flex items-center justify-center gap-4 my-4">
              <ButtonOutlined
                onClick={() => {
                  setIsOpenModal(false);
                  setIsCanEdit(false);
                }}
                mode="del"
                buttonName="ยกเลิก"
              />

              {!isCanEdit && (
                <ButtonPrimary
                  onClick={() => {
                    setIsCanEdit(true);
                  }}
                  buttonName={"แก้ไข"}
                  btnProps={{
                    width: "100px",
                  }}
                />
              )}
              {isCanEdit && (
                <ButtonPrimary
                  onClick={() => {
                    if (period_id) {
                      handleSubmitByEmployee(+period_id);
                    }
                  }}
                  mode="success"
                  buttonName="ยืนยัน"
                  disabled={loadingSubmit}
                />
              )}
            </article>
          )}
        </section>
      }
    />
  );
};

export default ModalQuotaHoliday;
